export interface TableSessionOpenCommand {
  kind: "OPEN_TABLE";
  tableId: string;
  tableNumber: string;
  expectedTableRevision: number;
  idempotencyKey: string;
  pricingPlanId: string | null;
  expectedPricingPlanVersion: number | null;
  pricingPlanName: string | null;
  customerRequestEventId: string | null;
}

export type TableSessionOpenRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface TableSessionOpenRecovery {
  ownerIdentityKey: string;
  command: TableSessionOpenCommand;
  state: TableSessionOpenRecoveryState;
}

export type TableSessionOpenRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: TableSessionOpenRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

export const TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY =
  "billiard-erp:table-session-open-recovery:v4";
const LEGACY_V3_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY =
  "billiard-erp:table-session-open-recovery:v3";
const LEGACY_V2_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY =
  "billiard-erp:table-session-open-recovery:v2";
const LEGACY_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY =
  "billiard-erp:table-session-open-recovery:v1";

const definiteNoEffectCodes = new Set([
  "AUTH_REQUIRED",
  "AUTHORIZATION_DEFAULT_DENY",
  "CSRF_REJECTED",
  "IDEMPOTENCY_COLLISION",
  "IDEMPOTENCY_KEY_REUSED",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "OFFLINE_AUTHORITY_UNAVAILABLE",
  "ORIGIN_REJECTED",
  "TABLE_ALREADY_IN_USE",
  "FRONTDESK_OPERATING_FACT_CONFLICT",
  "TABLE_NOT_FOUND",
  "TABLE_VERSION_CONFLICT",
  "INVALID_EXPECTED_TABLE_REVISION",
  "FRONTDESK_OPEN_SESSION_REQUEST_INVALID",
  "OPEN_TABLE_REQUEST_ID_INVALID",
  "OPEN_TABLE_REQUEST_NOT_FOUND",
  "OPEN_TABLE_REQUEST_TYPE_MISMATCH",
  "OPEN_TABLE_REQUEST_TABLE_MISMATCH",
  "OPEN_TABLE_REQUEST_TABLE_VERSION_CHANGED",
  "OPEN_TABLE_REQUEST_NOT_ACKNOWLEDGED",
  "OPEN_TABLE_REQUEST_ASSIGNEE_MISMATCH",
  "OPEN_TABLE_REQUEST_SESSION_INVALID",
  "OPEN_TABLE_REQUEST_REQUIRED",
  "OPEN_TABLE_PRICING_REQUIRED",
  "OPEN_TABLE_REQUEST_STATE_CHANGED",
  "OPEN_TABLE_REQUEST_RESULT_INVALID",
  "SERVICE_REQUEST_TERMINAL_SEQUENCE_INVALID",
  "SERVICE_DUTY_NOT_ACTIVE",
  "STORE_CLOSING_NEW_CUSTOMER_BLOCKED",
  "PRICING_PLAN_NOT_FOUND",
  "PRICING_PLAN_UNAVAILABLE",
  "PRICING_PLAN_VERSION_CONFLICT",
]);

let pendingRecovery: TableSessionOpenRecovery | null = null;
let dispatchingRecovery: TableSessionOpenRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;

export function tableSessionOpenIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function tableSessionOpenOutcomeIsUnknown(error: unknown): boolean {
  return !(
    isRecord(error) &&
    typeof error.code === "string" &&
    definiteNoEffectCodes.has(error.code)
  );
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && [...value].length >= minimum && [...value].length <= maximum;
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function isTableSessionOpenCommand(value: unknown): value is TableSessionOpenCommand {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "kind",
      "tableId",
      "tableNumber",
      "expectedTableRevision",
      "idempotencyKey",
      "pricingPlanId",
      "expectedPricingPlanVersion",
      "pricingPlanName",
      "customerRequestEventId",
    ])
  ) {
    return false;
  }
  const hasNoPricingSelection =
    value.pricingPlanId === null &&
    value.expectedPricingPlanVersion === null &&
    value.pricingPlanName === null;
  const hasCompletePricingSelection =
    typeof value.pricingPlanId === "string" &&
    /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/u.test(value.pricingPlanId) &&
    Number.isSafeInteger(value.expectedPricingPlanVersion) &&
    Number(value.expectedPricingPlanVersion) >= 1 &&
    isBoundedString(value.pricingPlanName, 1, 80) &&
    value.pricingPlanName === value.pricingPlanName.trim();
  return (
    value.kind === "OPEN_TABLE" &&
    isBoundedString(value.tableId, 1, 256) &&
    value.tableId === value.tableId.trim() &&
    isBoundedString(value.tableNumber, 1, 64) &&
    value.tableNumber === value.tableNumber.trim() &&
    Number.isSafeInteger(value.expectedTableRevision) &&
    Number(value.expectedTableRevision) >= 0 &&
    isBoundedString(value.idempotencyKey, 8, 128) &&
    value.idempotencyKey === value.idempotencyKey.trim() &&
    (value.customerRequestEventId === null ||
      (isBoundedString(value.customerRequestEventId, 8, 128) &&
        value.customerRequestEventId === value.customerRequestEventId.trim())) &&
    (hasNoPricingSelection || hasCompletePricingSelection) &&
    (value.customerRequestEventId === null || hasCompletePricingSelection)
  );
}

function isV3TableSessionOpenCommand(
  value: unknown,
): value is Omit<TableSessionOpenCommand, "customerRequestEventId"> {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "kind",
      "tableId",
      "tableNumber",
      "expectedTableRevision",
      "idempotencyKey",
      "pricingPlanId",
      "expectedPricingPlanVersion",
      "pricingPlanName",
    ])
  ) {
    return false;
  }
  return isTableSessionOpenCommand({ ...value, customerRequestEventId: null });
}

function isV2TableSessionOpenCommand(
  value: unknown,
): value is Omit<
  TableSessionOpenCommand,
  "pricingPlanId" | "expectedPricingPlanVersion" | "pricingPlanName" | "customerRequestEventId"
> {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "kind",
      "tableId",
      "tableNumber",
      "expectedTableRevision",
      "idempotencyKey",
    ])
  ) {
    return false;
  }
  return (
    value.kind === "OPEN_TABLE" &&
    isBoundedString(value.tableId, 1, 256) &&
    value.tableId === value.tableId.trim() &&
    isBoundedString(value.tableNumber, 1, 64) &&
    value.tableNumber === value.tableNumber.trim() &&
    Number.isSafeInteger(value.expectedTableRevision) &&
    Number(value.expectedTableRevision) >= 0 &&
    isBoundedString(value.idempotencyKey, 8, 128) &&
    value.idempotencyKey === value.idempotencyKey.trim()
  );
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: TableSessionOpenCommand,
  state: TableSessionOpenRecoveryState,
): TableSessionOpenRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function storedRecovery(value: unknown): TableSessionOpenRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 4 ||
    !isRecord(value.recovery)
  ) {
    return null;
  }
  const recovery = value.recovery;
  if (
    !hasExactKeys(recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(recovery.ownerIdentityKey) ||
    !isTableSessionOpenCommand(recovery.command) ||
    (recovery.state !== "CLAIMED" && recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(recovery.ownerIdentityKey, recovery.command, recovery.state);
}

function storedV3Recovery(value: unknown): TableSessionOpenRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 3 ||
    !isRecord(value.recovery)
  ) {
    return null;
  }
  const recovery = value.recovery;
  if (
    !hasExactKeys(recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(recovery.ownerIdentityKey) ||
    !isV3TableSessionOpenCommand(recovery.command) ||
    (recovery.state !== "CLAIMED" && recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(
    recovery.ownerIdentityKey,
    { ...recovery.command, customerRequestEventId: null },
    recovery.state,
  );
}

function storedV2Recovery(value: unknown): TableSessionOpenRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 2 ||
    !isRecord(value.recovery)
  ) {
    return null;
  }
  const recovery = value.recovery;
  if (
    !hasExactKeys(recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(recovery.ownerIdentityKey) ||
    !isV2TableSessionOpenCommand(recovery.command) ||
    (recovery.state !== "CLAIMED" && recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(
    recovery.ownerIdentityKey,
    {
      ...recovery.command,
      pricingPlanId: null,
      expectedPricingPlanVersion: null,
      pricingPlanName: null,
      customerRequestEventId: null,
    },
    recovery.state,
  );
}

function recoveryEquals(left: TableSessionOpenRecovery, right: TableSessionOpenRecovery): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredRecovery(storage: Storage): TableSessionOpenRecovery | null {
  const encoded = storage.getItem(TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredRecovery(recovery: TableSessionOpenRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 4, recovery }),
    );
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function verifyStoredRecovery(recovery: TableSessionOpenRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY);
    if (encoded === null) {
      const encodedV3 = access.storage.getItem(LEGACY_V3_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY);
      if (encodedV3 !== null) {
        const migratedV3 = storedV3Recovery(JSON.parse(encodedV3) as unknown);
        if (!migratedV3) {
          recoveryUnavailable = true;
          return;
        }
        const recovery =
          migratedV3.state === "CLAIMED"
            ? immutableRecovery(migratedV3.ownerIdentityKey, migratedV3.command, "OUTCOME_UNKNOWN")
            : migratedV3;
        if (!saveStoredRecovery(recovery)) {
          recoveryUnavailable = true;
          return;
        }
        access.storage.removeItem(LEGACY_V3_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY);
        if (access.storage.getItem(LEGACY_V3_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY) !== null) {
          recoveryUnavailable = true;
          return;
        }
        pendingRecovery = recovery;
        return;
      }
      const encodedV2 = access.storage.getItem(LEGACY_V2_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY);
      if (encodedV2 !== null) {
        const migratedV2 = storedV2Recovery(JSON.parse(encodedV2) as unknown);
        if (!migratedV2) {
          recoveryUnavailable = true;
          return;
        }
        const recovery =
          migratedV2.state === "CLAIMED"
            ? immutableRecovery(migratedV2.ownerIdentityKey, migratedV2.command, "OUTCOME_UNKNOWN")
            : migratedV2;
        if (!saveStoredRecovery(recovery)) {
          recoveryUnavailable = true;
          return;
        }
        access.storage.removeItem(LEGACY_V2_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY);
        if (access.storage.getItem(LEGACY_V2_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY) !== null) {
          recoveryUnavailable = true;
          return;
        }
        pendingRecovery = recovery;
        return;
      }
      if (access.storage.getItem(LEGACY_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
      }
      return;
    }
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovery =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(recovery)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovery;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readTableSessionOpenRecovery(identityKey: string): TableSessionOpenRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable || !isIdentityKey(identityKey)) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimTableSessionOpenRecovery(
  identityKey: string,
  command: TableSessionOpenCommand,
): TableSessionOpenRecoveryView {
  const current = readTableSessionOpenRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!isIdentityKey(identityKey) || !isTableSessionOpenCommand(command)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  pendingRecovery = recovery;
  return { status: "OWNED", recovery };
}

function verifyRecoveryForDispatch(
  recovery: TableSessionOpenRecovery,
  identityKey: string,
): boolean {
  hydrateStoredRecovery();
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    !verifyStoredRecovery(recovery)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function beginTableSessionOpenDispatch(
  recovery: TableSessionOpenRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery || !verifyRecoveryForDispatch(recovery, identityKey)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishTableSessionOpenDispatch(
  recovery: TableSessionOpenRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markTableSessionOpenOutcomeUnknown(
  recovery: TableSessionOpenRecovery,
  identityKey: string,
): boolean {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  return true;
}

export function clearTableSessionOpenRecovery(
  recovery: TableSessionOpenRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const stored = readStoredRecovery(access.storage);
      if (!stored || !recoveryEquals(stored, recovery)) {
        recoveryUnavailable = true;
        return false;
      }
      access.storage.removeItem(TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  return true;
}

export function resetTableSessionOpenRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY);
    access.storage?.removeItem(LEGACY_V3_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY);
    access.storage?.removeItem(LEGACY_V2_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY);
    access.storage?.removeItem(LEGACY_TABLE_SESSION_OPEN_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetTableSessionOpenRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
}
